import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useUpdateEffect } from 'react-use';
import i18n from '../../../base-dictionary';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { PageLayout } from '../../../components/page-layout/page-layout';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useStrategyStore, useThroughputTabStore } from '../../../store/strategy-store/strategy-store';
import {
  resetInitiativePerformanceState,
  setActivePanel,
  setFlowByPhaseSelectedEpic,
  setInitiatives,
  setKPIOverviewData,
  setPerformanceActivePanel,
  setSelectedInitiative,
} from '../../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../../store/strategy-store/strategy-store.type';
import { useFlora } from '../../flora/flora.context';
import { FloraSupportedObjectTypes } from '../../flora/flora.type';
import { MenuDrivenContainer } from '../../menu-driven-container/menu-driven-container';
import { toast } from '../../notifications';
import { GeneralViewNames } from '../../side-bar/side-bar.type';
import { InitiativeModal } from '../initiative-modal/initiative-modal';
import { InitiativeModalMode } from '../initiative-modal/initiative-modal.type';
import { useFlowByPhaseData } from './flow-by-phase-tab/flow-by-phase-tab.hooks';
import { InitiativePerformanceControlBar } from './initiative-performance-control-bar';
import { InitiativePerformanceFilters } from './initiative-performance-filters';
import { InitiativePerformanceHeader } from './initiative-performance-header';
import { InitiativePerformanceMainSection } from './initiative-performance-main-section';
import { InitiativePerformanceModal } from './initiative-performance-modal';
import { InitiativePerformanceOverview } from './initiative-performance-overview';
import { InitiativePerformanceSideSection } from './initiative-performance-side-section';
import { InitiativePerformanceTabs } from './initiative-performance-tabs';
import {
  useDeleteInitiative,
  useEditInitiative,
  useInitiativeFocusData,
  useInitiativePerformanceData,
  useSideSection,
} from './initiative-performance.hooks';
import { PerformanceTab, ThroughputMode } from './initiative-performance.type';
import { useTargetsData } from './targets-tab/targets-tab.hooks';

export const InitiativePerformance = () => {
  const navigate = useNavigate();

  const portfolio = useGlobalStore((state) => state.portfolio);
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const performanceTab = useStrategyStore((state) => state.performanceTab);
  const throughputMode = useThroughputTabStore((state) => state.mode);
  const performanceActivePanel = useStrategyStore((state) => state.performanceActivePanel);
  const { setContextualInfo } = useFlora();

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    if (initiative) {
      setContextualInfo({
        currentModule: GeneralViewNames.Strategy,
        currentObjects: [{ type: FloraSupportedObjectTypes.Initiative, id: initiative.id, name: initiative.name }],
        startDate: initiative.start_date ?? null,
        endDate: initiative.end_date ?? null,
      });
    }
  }, [initiative, setContextualInfo]);

  const { isLoading } = useInitiativePerformanceData();
  useInitiativeFocusData();
  useFlowByPhaseData();
  useTargetsData();

  const showSideFilters = performanceActivePanel === Panel.Filters;
  const showSideSection = useSideSection();

  const editInitiativeMutation = useEditInitiative({
    portfolioId: portfolio?.id,
    initiativeId: initiative?.id,
    onSuccess: () => {
      setIsEditModalOpen(false);
      setFlowByPhaseSelectedEpic(null);
    },
  });

  const deleteInitiativeMutation = useDeleteInitiative({
    portfolioId: portfolio?.id,
    initiativeId: initiative?.id,
    onSuccess: () => {
      toast.success(i18n.t('strategy.initiatives.deleted_success', { name: initiative?.name }));
      setSelectedInitiative(null);
      setActivePanel(Panel.Filters);
      setIsEditModalOpen(false);
      navigate('/application/strategy', { replace: true });
    },
  });

  useUpdateEffect(() => {
    // Only reset and navigate if we're already on the initiative performance page
    // and the portfolio actually changed to a different one
    if (portfolio && initiative && initiative.portfolio !== portfolio.id) {
      setInitiatives([]);
      setKPIOverviewData(null);
      setActivePanel(Panel.Filters);
      navigate('/application/strategy', {
        replace: true,
      });
    }
  }, [portfolio?.id]);

  useEffect(() => {
    return () => {
      resetInitiativePerformanceState();
    };
  }, []);

  const filtersTitle =
    performanceTab === PerformanceTab.Throughput && throughputMode === ThroughputMode.InitiativeProgress
      ? i18n.t('strategy.initiative_performance.details_title')
      : i18n.t('common.filters');

  const setShowSideFilters = (show: boolean) => {
    setPerformanceActivePanel(show ? Panel.Filters : null);
  };

  return (
    <MenuDrivenContainer header={<InitiativePerformanceHeader />}>
      <BrandedLoadingOverlay visible={isLoading} variant="colored" transitionDuration={30} />
      <PageLayout
        controlBar={
          <InitiativePerformanceControlBar openInfoModal={setIsInfoModalOpen} openEditModal={setIsEditModalOpen} />
        }
        overview={{
          title: i18n.t('strategy.overview.title'),
          content: <InitiativePerformanceOverview />,
        }}
        tabs={<InitiativePerformanceTabs />}
        sideFilters={{
          title: filtersTitle,
          content: <InitiativePerformanceFilters setShow={setShowSideFilters} />,
          show: showSideFilters,
          setShow: setShowSideFilters,
        }}
        mainSection={<InitiativePerformanceMainSection />}
        sideSection={<InitiativePerformanceSideSection />}
        showSideSection={showSideSection}
        data-page-layout
      />
      <InitiativePerformanceModal opened={isInfoModalOpen} onClose={() => setIsInfoModalOpen(false)} />
      {initiative && (
        <InitiativeModal
          mode={InitiativeModalMode.Edit}
          initiative={initiative}
          opened={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={editInitiativeMutation.mutateAsync}
          isSubmitting={editInitiativeMutation.isPending}
          onDelete={deleteInitiativeMutation.mutateAsync}
          isDeleting={deleteInitiativeMutation.isPending}
        />
      )}
    </MenuDrivenContainer>
  );
};
